import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { useParams, Link } from 'react-router-dom';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getSitesByCategory } from '@/mocks/sites';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const sites = getSitesByCategory(slug || '');

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-[400px] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 柠檬导航`,
    description: category.description,
    url: `https://www.lemonnav.cn/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: sites.map((site, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'WebSite',
          name: site.name,
          url: site.url,
          description: site.description,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 柠檬导航 | 精选${category.name}网址推荐`}
        description={`柠檬导航${category.name}分类：${category.description}。收录${category.count}个精选${category.name}网站，涵盖常用工具、优质资源等。`}
        keywords={`${category.name},柠檬导航,网址导航,网站推荐,${category.name}网站大全,实用网站`}
        canonicalUrl={`https://www.lemonnav.cn/category/${category.slug}`}
        schemaData={categorySchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="mt-4 flex items-center gap-4">
              <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl ${category.color} flex items-center justify-center`}>
                <i className={`${category.icon} text-2xl md:text-3xl`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Category tabs */}
        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-none">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Sites grid */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {sites.map((site) => (
              <a
                key={site.id}
                href={site.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="group bg-background-50 rounded-xl border border-background-200/70 p-5 hover:border-primary-300 hover:shadow-md transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105">
                    <i className={`${site.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-1.5">
                      {site.name}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      {site.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  {site.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[10px] md:text-xs whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                  {site.isFeatured && (
                    <span className="px-1.5 py-0.5 rounded-md bg-accent-100 text-accent-700 text-[10px] font-medium whitespace-nowrap ml-auto">
                      推荐
                    </span>
                  )}
                </div>
              </a>
            ))}
          </div>

          {sites.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-inbox-line text-4xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类暂无网站，敬请期待</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}